/**
 * Les gestes qui changent le document d'UN résident exact (#1050, #1333).
 *
 * Toute écriture passe par une **cible capturée avant le réseau** et revalidée
 * au commit : un callback B parqué écrit son scope B, jamais « le canvas
 * courant » retrouvé par recherche. Depuis #1333 il n'y a plus de seconde
 * publication : la présentation vit dans le scope, ses consommateurs
 * l'observent, et rien ne la recopie plus dans un store d'écran.
 */

import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import type {
  CanvasExclusiveReplacementLease,
  CanvasRuntimeDocumentReplacementDecision,
  CanvasRuntimeDocumentReplacementInput,
} from './canvasDocumentRuntime';
import type { CanvasSessionCallbackIdentity } from './canvasSessionRegistry';
import type { CanvasTemplateMeta } from './canvasEditorStore';
import type { CanvasDocumentDepart, CanvasDraft } from '@hector/shared-types';
import {
  PRESENTATION_VIERGE,
  presentationDUnBrouillonRestaure,
  presentationDUnePageNeuve,
  type CanvasDocumentPresentationSnapshot,
} from './canvasDocumentScope';
import {
  type CanvasDraftRecoveryState,
} from './canvasDraftController';
import { prepareCanvasDocx } from './prepareCanvasDocx';

/**
 * Le scope monté de cette conversation, ou un refus qui nomme la conversation.
 *
 * ⚠️ **La confrontation de propriétaire a été RETIRÉE, pas migrée** (#1336).
 * Elle cherchait le scope par `documentSessionId` puis comparait son
 * propriétaire à celui qu'annonçait l'appelant ; le directory étant indexé par
 * la conversation, cette comparaison rendrait la même valeur avec le défaut et
 * sans lui — la tautologie que le lot C a déjà retirée de `requireTarget`. Ce
 * qui tranche encore : ce document est-il monté ?
 */
function requireExactScope(conversationId: string) {
  const scope = canvasDocumentRuntime.getScope(conversationId);
  if (!scope) {
    throw new Error(
      `Canvas resident presentation refused for conversationId=${conversationId}: no mounted scope. `
      + 'Acquire and mount the canvas document of this conversation before applying '
      + 'the document replacement.',
    );
  }
  return scope;
}

function currentPresentation(conversationId: string): CanvasDocumentPresentationSnapshot {
  return requireExactScope(conversationId).getPresentation() ?? PRESENTATION_VIERGE;
}

/**
 * Cible immuable d'un geste documentaire : elle est capturée avant tout réseau
 * et revalidée par le runtime au commit. Elle ne désigne jamais « le canvas
 * courant » après un changement de conversation.
 */
export type CanvasResidentReplacementTarget = {
  readonly identity: CanvasSessionCallbackIdentity;
};

/** Exact live DOCX and presentation needed to restore A if mounting B fails. */
export type CanvasResidentRecoverySnapshot = {
  readonly identity: CanvasSessionCallbackIdentity;
  readonly documentId: string | null;
  readonly presentation: CanvasDocumentPresentationSnapshot;
  readonly draft: CanvasDraftRecoveryState;
};

export function captureCanvasResidentReplacementTarget(input: {
  readonly conversationId: string;
}): CanvasResidentReplacementTarget {
  const scope = requireExactScope(input.conversationId);
  const snapshot = scope.getSnapshot();
  return {
    identity: {
      conversationId: snapshot.conversationId,
      // ⚠️ L'époque est LUE sur le montage, jamais reprise de l'entrée : c'est
      // elle seule qui distingue le montage capturé par le geste d'un remontage
      // survenu pendant le réseau qui suit.
      mountEpoch: snapshot.mountEpoch,
    },
  };
}

function presentationForTarget(target: CanvasResidentReplacementTarget): CanvasDocumentPresentationSnapshot {
  return currentPresentation(target.identity.conversationId);
}

/**
 * Exports the resident before a destructive remount.
 *
 * The presentation's original seed is insufficient: it predates live lawyer
 * edits. The exported DOCX is therefore fenced by the same mount identity both
 * before and after the async provider call.
 *
 * **Câblée par le chargement agent à chaud, et par lui seul** (#1338, D4 ; D-C) :
 * `replaceResidentCanvasDocument` (`WebSocketPool.ts`) capture A avant de
 * commiter B, puis restaure A par `restoreCanvasResidentAfterFailedReplacement`
 * si B ne devient pas prêt. Les gestes humains et l'admission d'un travail
 * commitent sans ce filet (#1431). Gardes : `canvasResidentPresentation.test.ts`
 * (bloc « reprise de A ») et, au seam du transport sur le vrai runtime,
 * `WebSocketPool.repriseDuDocument.test.ts`.
 *
 * ⚠️ Toujours un EXPORT sur ce chemin, jamais la graine d'ouverture : un
 * résident chaud a été prêt, donc a exposé un éditeur, et
 * `peutReprendreLaSourceInitiale` y est structurellement faux (voir le
 * docblock de `replaceResidentCanvasDocument`).
 */
export async function captureCanvasResidentRecovery(
  target: CanvasResidentReplacementTarget,
): Promise<CanvasResidentRecoverySnapshot> {
  const scope = requireExactScope(target.identity.conversationId);
  const before = scope.getSnapshot();
  if (
    before.state !== 'ready'
    || before.mountEpoch !== target.identity.mountEpoch
    || !before.instance
  ) {
    throw new Error(
      `Canvas recovery capture refused for conversationId=${target.identity.conversationId}: `
      + `expected ready mountEpoch=${target.identity.mountEpoch}, received state=${before.state} `
      + `mountEpoch=${before.mountEpoch}. Retry after inspecting the exact resident.`,
    );
  }
  const controller = scope.getDraftController();
  const capturedVersion = controller.getSnapshot().version;
  const exported = await controller.exportDocx(before.instance);
  const prepared = await prepareCanvasDocx(new Uint8Array(await exported.arrayBuffer()), before.instance);
  // Validation itself awaits public document reads: fence after it as well as export.
  const blob = prepared.status === 'repaired'
    ? new Blob([new Uint8Array(prepared.bytes)], { type: exported.type }) : exported;
  const after = scope.getSnapshot();
  if (
    after.state !== 'ready'
    || after.mountEpoch !== before.mountEpoch
    || after.instance !== before.instance
    || controller.getSnapshot().version !== capturedVersion
  ) {
    throw new Error(
      `Canvas recovery capture became stale for conversationId=${before.conversationId}: `
      + `captured mountEpoch=${before.mountEpoch}, current mountEpoch=${after.mountEpoch} state=${after.state} capturedVersion=${capturedVersion} currentVersion=${controller.getSnapshot().version}. `
      + 'No replacement was started; retry from the current document.',
    );
  }
  if (prepared.status === 'rejected') {
    throw new Error(
      `Canvas recovery capture exported a non-savable DOCX for conversationId=${before.conversationId} (${prepared.issues.map(issue => issue.code).join(', ')}). `
      + 'No replacement was started; retry after the editor is ready.',
    );
  }
  return {
    identity: {
      conversationId: before.conversationId,
      mountEpoch: before.mountEpoch,
    },
    documentId: before.documentId,
    presentation: {
      ...presentationForTarget(target),
      templateOverride: blob,
    },
    draft: scope.getDraftController().captureRecoveryState(),
  };
}

function commitReplacement(
  input: Omit<CanvasRuntimeDocumentReplacementInput, 'presentation'> & {
    readonly presentation: CanvasDocumentPresentationSnapshot;
    readonly replacementLease?: CanvasExclusiveReplacementLease;
  },
): CanvasRuntimeDocumentReplacementDecision {
  return canvasDocumentRuntime.replaceDocument(input);
}

/**
 * Applies a downloaded DOCX to its captured resident, never to the current slot by lookup.
 *
 * ⚠️ **Un remplacement ne reporte PAS l'identité de l'ancien document.** La
 * première version fusionnait (`{ ...precedente, templateOverride, templateMeta }`)
 * et gardait donc, sous le nouveau document, le renommage de l'ancien et surtout
 * son `activeDraft` — c'est-à-dire son `kind` et son `sourceDocumentId`. La pièce
 * B fraîchement chargée s'annonçait alors « acte généré » au modèle et
 * s'enregistrait sous le brouillon de A. Ce qui survit à un remplacement, c'est
 * la place — le résident de ce fil, remonté sous une époque neuve —, pas ce
 * qui était posé dessus.
 */
export function replaceCanvasResidentTemplate(input: {
  readonly target: CanvasResidentReplacementTarget;
  readonly blob: Blob;
  readonly meta: CanvasTemplateMeta;
  readonly replacementLease?: CanvasExclusiveReplacementLease;
}): CanvasRuntimeDocumentReplacementDecision {
  const presentation: CanvasDocumentPresentationSnapshot = {
    templateOverride: input.blob,
    templateMeta: input.meta,
    documentTitle: null,
    activeDraft: null,
    draftNeedsHydration: false,
  };
  return commitReplacement({
    identity: input.target.identity,
    documentId: input.meta.documentId,
    presentation,
    replacementLease: input.replacementLease,
  });
}

/**
 * Bascule le résident vers un AUTRE brouillon **de la même conversation**, en
 * ADOPTANT son identité (2026-09-22).
 *
 * ⚠️ **C'est l'inverse exact de `replaceCanvasResidentTemplate`, et la
 * distinction est tout le sujet.** Celui-ci pose `activeDraft: null` : le
 * document chargé est une COPIE, et l'auto-save du fil repart sous son propre
 * `draftId`. C'est correct pour un brouillon d'un AUTRE fil, dont l'original
 * doit rester chez lui — et qui ne peut de toute façon pas changer de
 * conversation (`requireMonotoneDraftScope`, côté serveur).
 *
 * Pour un brouillon du fil COURANT, ce serait un défaut : basculer de A vers B
 * réenregistrerait le contenu de B sous un TROISIÈME identifiant, et l'avocat
 * accumulerait des copies jusqu'au plafond sans comprendre pourquoi. La
 * présentation porte donc `activeDraft`, et l'appelant fait ensuite adopter son
 * `draftId` au contrôleur.
 *
 * `draftNeedsHydration: false` : les octets sont déjà là (`templateOverride`),
 * les redemander au serveur rejouerait le GET que l'appelant vient de faire.
 */
export function basculerVersUnBrouillonDuFil(input: {
  readonly target: CanvasResidentReplacementTarget;
  readonly blob: Blob;
  readonly draft: CanvasDraft;
  readonly replacementLease?: CanvasExclusiveReplacementLease;
}): CanvasRuntimeDocumentReplacementDecision {
  const presentation: CanvasDocumentPresentationSnapshot = {
    ...presentationDUnBrouillonRestaure(input.draft),
    templateOverride: input.blob,
    draftNeedsHydration: false,
  };
  return commitReplacement({
    identity: input.target.identity,
    documentId: input.draft.sourceDocumentId,
    presentation,
    replacementLease: input.replacementLease,
  });
}

/**
 * Remonte A, exporté avant le commit, à la place d'un candidat B qui n'est pas
 * devenu prêt (#1338, D4).
 *
 * ⚠️ **L'époque du candidat est EXIGÉE, jamais relue sur le montage vivant**
 * (D3). La première version remontait par-dessus « ce qui est monté
 * maintenant » : une restauration tardive écrasait alors un remontage survenu
 * APRÈS l'échec de B — un autre document, que personne n'a demandé de quitter.
 * Avec l'époque exacte, `replaceDocument` le refuse (`mount-epoch-mismatch`).
 *
 * **Le bail est facultatif, et c'est la décision D-E.** Fourni (épingle du tour
 * encore vivante, présentée), il vaut exemption pour CE tour. Absent (le tour
 * s'est terminé et a rendu son épingle), la restauration n'a aucune exemption :
 * un document que protège un autre travail est laissé tel quel, et le refus
 * `session-protected` remonte à l'appelant.
 *
 * Une restauration fait donc avancer l'époque DEUX fois depuis A : B, puis A′.
 * L'appelant attend A′ sur l'époque que CETTE décision a allouée.
 */
export function restoreCanvasResidentAfterFailedReplacement(input: {
  readonly recovery: CanvasResidentRecoverySnapshot;
  /** L'époque que le commit de B a allouée — le seul montage que ce retour peut remplacer. */
  readonly failedCandidateMountEpoch: number;
  readonly replacementLease?: CanvasExclusiveReplacementLease;
}): CanvasRuntimeDocumentReplacementDecision {
  const scope = requireExactScope(input.recovery.identity.conversationId);
  const decision = commitReplacement({
    identity: {
      conversationId: input.recovery.identity.conversationId,
      mountEpoch: input.failedCandidateMountEpoch,
    },
    documentId: input.recovery.documentId,
    presentation: input.recovery.presentation,
    replacementLease: input.replacementLease,
  });
  if (decision.accepted) {
    // The presentation and the autosave identity are one transaction. If only
    // the DOCX were restored, A would be visible while the next save still used
    // B's title/source metadata and retryable payload.
    scope.getDraftController().restoreRecoveryState(input.recovery.draft);
  }
  return decision;
}

/**
 * Pose une page neuve à la place du document du résident capturé.
 *
 * @param input.depart — le papier à en-tête dont elle part (absent = le défaut du cabinet). Il
 *   voyage dans la présentation ; c'est le montage alloué par ce remplacement qui le lit.
 */
export function startBlankCanvasResident(input: {
  readonly target: CanvasResidentReplacementTarget;
  readonly replacementLease?: CanvasExclusiveReplacementLease;
  readonly depart?: CanvasDocumentDepart;
}): CanvasRuntimeDocumentReplacementDecision {
  return commitReplacement({
    identity: input.target.identity,
    documentId: null,
    presentation: presentationDUnePageNeuve(input.depart),
    replacementLease: input.replacementLease,
  });
}

/**
 * Pose le document de l'avocat remis sur un autre papier à en-tête (« Changer de papier à en-tête »,
 * 2026-10-14), comme un document NEUF du fil.
 *
 * Neuf, et non le même brouillon réécrit : le document d'avant reste dans « Documents de
 * cette conversation », ce qui rend le geste réversible sans rien inventer, et le chemin est
 * celui de « Nouveau », déjà éprouvé sur le vrai runtime. Le titre suit le document : sans
 * lui, l'avocat retrouverait son acte sous « Nouveau document ».
 *
 * `templateOverride` porte le document COMPLET, pas un papier vide : le premier
 * enregistrement qu'il arme est voulu, c'est celui du nouveau document. Aucun `depart` :
 * celui-ci ne sert qu'à charger un papier vide au montage d'une page neuve.
 */
export function startCanvasResidentOnPageModel(input: {
  readonly target: CanvasResidentReplacementTarget;
  readonly replacementLease?: CanvasExclusiveReplacementLease;
  readonly blob: Blob;
  readonly titre: string | null;
}): CanvasRuntimeDocumentReplacementDecision {
  return commitReplacement({
    identity: input.target.identity,
    documentId: null,
    presentation: { ...PRESENTATION_VIERGE, templateOverride: input.blob, documentTitle: input.titre },
    replacementLease: input.replacementLease,
  });
}

/**
 * Le renommage au double-clic, écrit là où le titre est LU (#1333).
 *
 * Il allait auparavant dans la projection d'écran, que le contexte agent ne
 * lisait pas : le document portait deux noms selon la surface interrogée. Aucun
 * remontage ici — renommer n'échange pas le document, donc aucune époque n'est
 * consommée. Le marquage « modifié » reste au geste appelant, qui possède le
 * contrôleur de brouillon exact.
 */
export function renommerLeDocumentDuResident(input: {
  readonly conversationId: string;
  readonly titre: string | null;
}): void {
  const scope = requireExactScope(input.conversationId);
  const previous = scope.getPresentation() ?? PRESENTATION_VIERGE;
  const titre = input.titre?.trim() ?? '';
  scope.capturePresentation({ ...previous, documentTitle: titre.length > 0 ? titre : null });
}

/**
 * Le brouillon de CE fil que la génération doit consommer — hydraté, ou dont
 * l'hydratation est encore en vol.
 *
 * Le `draftId` persistant n'est PAS touché : l'acte doit remplacer le même
 * brouillon. Ce qui part, c'est la source montée et l'autorisation qu'aurait
 * une réponse `/content` encore en vol de la reposer par-dessus l'acte qui
 * commence.
 */
function brouillonDeCeFilAConsommer(
  conversationId: string,
  presentation: CanvasDocumentPresentationSnapshot,
): boolean {
  const draftId = canvasDocumentRuntime.getDraftController(conversationId)?.getSnapshot().draftId ?? null;
  const estLeBrouillonDeCeFil = presentation.activeDraft?.conversationId === conversationId
    && presentation.activeDraft.id === draftId;
  const estHydrate = estLeBrouillonDeCeFil
    && presentation.templateOverride !== null
    && presentation.templateMeta !== null
    && presentation.templateMeta.conversationId === conversationId
    // ⚠️ Ne JAMAIS élargir aux Word importés : leur gabarit doit précisément
    // survivre au démarrage d'un flux, sans quoi une génération lancée sur une
    // pièce ouverte la ferait disparaître de l'écran.
    && presentation.templateMeta.sourceFilePath == null
    && !presentation.draftNeedsHydration;
  const hydratationEnVol = estLeBrouillonDeCeFil
    && presentation.draftNeedsHydration
    && presentation.templateOverride === null
    && presentation.templateMeta === null;
  return estHydrate || hydratationEnVol;
}

/**
 * Le document qu'un travail de rédaction REMPLACE, ou `null` s'il écrit dans le
 * document courant (#1337).
 *
 * Depuis que l'époque de montage est la seule identité de remontage, plus aucun
 * compteur d'affichage ne reconstruit l'éditeur quand un nouveau travail
 * apparaît : c'est l'ADMISSION qui remplace le document, par le runtime
 * — donc sous une époque neuve — AVANT le reçu (`canvasAdmission.ts`). Cette
 * fonction décide seulement QUOI monter à la place ; l'admission décide quand, et
 * sous quelle épingle.
 *
 * Les cas reprennent exactement ce que l'affichage faisait avant ce ticket :
 *  - le brouillon de CE fil, pour une génération : consommé — gabarit retiré,
 *    `draftNeedsHydration` éteint, `draftId` persistant intact ;
 *  - un document SANS gabarit (plan streamé ici, page vierge) : une génération
 *    le remplace toujours ; une recherche seulement s'il porte déjà la production
 *    d'un travail de ce fil. Une recherche sur un document où rien n'a été
 *    rédigé ne remonte rien — critère « Recherche sans remplacement » ;
 *  - tout autre gabarit posé — Word importé, gabarit d'une autre conversation,
 *    hydratation en vol d'un autre brouillon — SURVIT, et le travail écrit
 *    dedans comme avant.
 *
 * ⚠️ `aDejaRedigeIci` est un signal de SESSION : après un rechargement, le hub
 * d'écoute est vide. Ce n'est pas un signal négatif trompeur ici — un document
 * rouvert après rechargement l'est depuis son brouillon, donc porte un gabarit,
 * et relève du troisième cas quel que soit ce signal.
 *
 * `depart` est le papier à en-tête que la génération HÉRITE du plan (porté par le
 * serveur, `request_params.canvasDepart` du job de plan) : le document qu'elle
 * pose part de ce modèle. Sans lui, un plan rouvert après rechargement — dont la
 * présentation, restaurée d'un brouillon, ne dit pas de quel modèle il est
 * parti — faisait écrire l'acte sur le défaut du cabinet. Une recherche qui
 * porte un modèle ne passe pas par ici : elle démarre toujours un document neuf
 * (`canvasAdmission.ts`).
 */
export function presentationDeRemplacementPourUnTravail(input: {
  readonly conversationId: string;
  readonly kind: 'research' | 'generation';
  /** Un travail de CE fil a-t-il déjà été affiché dans ce canvas depuis l'ouverture de l'onglet ? */
  readonly aDejaRedigeIci: boolean;
  readonly depart?: CanvasDocumentDepart;
}): CanvasDocumentPresentationSnapshot | null {
  const remplacante = presentationDeRemplacementSansDepart(input);
  if (remplacante === null || input.depart === undefined) return remplacante;
  return { ...remplacante, depart: input.depart };
}

function presentationDeRemplacementSansDepart(input: {
  readonly conversationId: string;
  readonly kind: 'research' | 'generation';
  readonly aDejaRedigeIci: boolean;
}): CanvasDocumentPresentationSnapshot | null {
  const presentation = requireExactScope(input.conversationId).getPresentation() ?? PRESENTATION_VIERGE;
  // Rien à avancer : c'est l'époque que `replaceDocument` alloue qui
  // reconstruit l'éditeur (#1337), pas un compteur porté par la présentation.
  if (input.kind === 'generation' && brouillonDeCeFilAConsommer(input.conversationId, presentation)) {
    return {
      ...presentation,
      templateOverride: null,
      templateMeta: null,
      // Explicite : le gabarit qui vient d'être retiré ne doit pas réarmer
      // l'hydratation avant que la génération publie son job.
      draftNeedsHydration: false,
    };
  }
  const sansGabarit = presentation.templateOverride === null
    && presentation.templateMeta === null
    && !presentation.draftNeedsHydration;
  if (!sansGabarit) return null;
  return input.kind === 'generation' || input.aDejaRedigeIci ? presentation : null;
}

/**
 * Remplace le document du résident EXACT par celui d'un travail (#1337).
 *
 * La cible est l'identité capturée AVANT la capture du plan : un remontage
 * survenu entre-temps fait refuser le remplacement (`mount-epoch-mismatch`) au
 * lieu d'effacer un document dont personne n'a lu le contenu.
 */
export function remplacerLeDocumentPourUnTravail(input: {
  readonly target: CanvasResidentReplacementTarget;
  readonly presentation: CanvasDocumentPresentationSnapshot;
  readonly replacementLease?: CanvasExclusiveReplacementLease;
}): CanvasRuntimeDocumentReplacementDecision {
  return commitReplacement({
    identity: input.target.identity,
    documentId: input.presentation.templateMeta?.documentId ?? null,
    presentation: input.presentation,
    replacementLease: input.replacementLease,
  });
}
